/* Progressive enhancements for keyboard access and project planning. */
(() => {
  'use strict';
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]';

  const heroSlideshows = new WeakMap();
  function enhanceHeroSlideshow(root) {
    if (heroSlideshows.has(root)) return;
    const slides = [...root.querySelectorAll('[data-hero-slide]')];
    const selector = root.querySelector('[data-hero-select]');
    const controls = root.querySelector('[data-hero-controls]');
    const toggle = root.querySelector('[data-hero-toggle]');
    const track = root.querySelector('[data-hero-slides]');
    const title = root.querySelector('[data-hero-title]');
    const location = root.querySelector('[data-hero-location]');
    if (slides.length < 2 || !controls || !toggle || !track) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const controller = new AbortController();
    const on = (target, event, handler) => target.addEventListener(event, handler, { signal: controller.signal });
    const interval = Math.max(4000, Number(root.dataset.interval) || 6000);
    let index = 0;
    let targetIndex = 0;
    let request = 0;
    let timer;
    let paused = root.dataset.autoplay === 'false' || motion.matches;
    let hovered = false;
    let focused = root.contains(document.activeElement);
    let visible = true;
    let destroyed = false;
    const canPlay = () => !destroyed && !paused && !hovered && !focused && visible && !document.hidden && root.isConnected;
    const syncPlayback = () => {
      toggle.setAttribute('aria-label', paused ? 'Play slideshow' : 'Pause slideshow');
      toggle.querySelector('[data-hero-play]').toggleAttribute('hidden', !paused);
      toggle.querySelector('[data-hero-pause]').toggleAttribute('hidden', paused);
      track.setAttribute('aria-live', paused || focused ? 'polite' : 'off');
    };
    const schedule = () => {
      clearTimeout(timer);
      root.dataset.playback = canPlay() ? 'running' : 'paused';
      if (canPlay()) timer = setTimeout(() => show(index + 1), interval);
    };
    const loadImage = async (slideIndex) => {
      const image = slides[slideIndex].querySelector('img');
      if (!image) return false;
      image.loading = 'eager';
      if (image.dataset.heroSrc && !image.getAttribute('src')) image.src = image.dataset.heroSrc;
      try { await image.decode(); } catch (_) { /* Keep the current image if a slide cannot load. */ }
      return image.complete && image.naturalWidth > 0;
    };
    const show = async (next, manual = false) => {
      clearTimeout(timer);
      if (manual) { paused = true; syncPlayback(); }
      targetIndex = (next + slides.length) % slides.length;
      const destination = targetIndex;
      const currentRequest = ++request;
      const loaded = await loadImage(destination);
      if (currentRequest !== request || destroyed || !root.isConnected) return;
      if (!loaded || (!manual && !canPlay())) {
        targetIndex = index;
        if (selector) selector.value = String(index);
        schedule();
        return;
      }
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === destination;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      if (selector) selector.value = String(destination);
      index = destination;
      title.textContent = slides[index].dataset.title;
      if (location) location.textContent = slides[index].dataset.location;
      // Fetch the next photograph before its turn so a slow connection never reveals a blank slide.
      void loadImage((index + 1) % slides.length);
      schedule();
    };

    on(root.querySelector('[data-hero-prev]'), 'click', () => show(targetIndex - 1, true));
    on(root.querySelector('[data-hero-next]'), 'click', () => show(targetIndex + 1, true));
    if (selector) on(selector, 'change', () => show(Number(selector.value), true));
    on(toggle, 'click', () => { paused = !paused; syncPlayback(); schedule(); });
    on(root, 'pointerenter', event => { if (event.pointerType === 'mouse') { hovered = true; schedule(); } });
    on(root, 'pointerleave', () => { hovered = false; schedule(); });
    on(root, 'focusin', () => { focused = true; syncPlayback(); schedule(); });
    on(root, 'focusout', event => { focused = root.contains(event.relatedTarget); syncPlayback(); schedule(); });
    on(controls, 'keydown', event => {
      if (event.target === selector) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const destination = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : targetIndex + (event.key === 'ArrowRight' ? 1 : -1);
      void show(destination, true);
    });
    on(document, 'visibilitychange', schedule);
    on(motion, 'change', () => { if (motion.matches) paused = true; syncPlayback(); schedule(); });
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      schedule();
    }, { threshold: 0 }) : null;
    observer?.observe(root);
    heroSlideshows.set(root, () => {
      destroyed = true;
      clearTimeout(timer);
      request++;
      controller.abort();
      observer?.disconnect();
      heroSlideshows.delete(root);
    });
    controls.hidden = false;
    syncPlayback();
    void loadImage(0).then(() => { void loadImage(1); schedule(); });
  }
  const initHeroSlideshows = scope => {
    if (scope.matches?.('[data-hero-slideshow]')) enhanceHeroSlideshow(scope);
    scope.querySelectorAll('[data-hero-slideshow]').forEach(enhanceHeroSlideshow);
  };
  initHeroSlideshows(document);
  document.addEventListener('shopify:section:load', event => initHeroSlideshows(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-hero-slideshow]').forEach(root => heroSlideshows.get(root)?.());
  });

  function enhanceDrawer(id, panelSelector, openerSelector, closeSelector, label) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    const panel = drawer.querySelector(panelSelector);
    const openers = document.querySelectorAll(openerSelector);
    let returnFocus;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', label);
    panel.tabIndex = -1;
    openers.forEach(button => {
      button.setAttribute('aria-controls', id);
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => { returnFocus = button; });
    });
    let wasOpen = false;
    const sync = () => {
      const isOpen = drawer.getAttribute('aria-hidden') === 'false';
      drawer.inert = !isOpen;
      openers.forEach(button => button.setAttribute('aria-expanded', String(isOpen)));
      if (isOpen && !wasOpen) {
        returnFocus = returnFocus || document.activeElement;
        (panel.querySelector(closeSelector) || panel).focus();
      } else if (!isOpen && wasOpen && returnFocus) {
        returnFocus.focus();
      }
      wasOpen = isOpen;
    };
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
    sync();
    drawer.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        drawer.querySelector(closeSelector)?.click();
      }
      if (event.key !== 'Tab') return;
      const items = [...panel.querySelectorAll(focusable)].filter(el => el.getClientRects().length && !el.closest('[aria-hidden="true"]'));
      if (!items.length) { event.preventDefault(); panel.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }
  enhanceDrawer('mobile-nav', '.mobile-nav__panel', '[data-mobile-nav-open]', '[data-mobile-nav-close]', 'Main navigation');
  enhanceDrawer('cart-drawer', '.cart-drawer__panel', '[data-cart-open]', '[data-cart-close]', 'Shopping cart');

  document.querySelectorAll('.mobile-nav__submenu').forEach(submenu => {
    const parent = submenu.closest('.mobile-nav__item').querySelector('[data-mobile-submenu]');
    const sync = () => {
      const open = submenu.getAttribute('aria-hidden') === 'false';
      submenu.inert = !open;
      parent.setAttribute('aria-expanded', String(open));
    };
    sync();
    new MutationObserver(sync).observe(submenu, { attributes: true, attributeFilter: ['aria-hidden'] });
    parent.addEventListener('click', () => { submenu.inert = false; submenu.querySelector('button, a')?.focus(); });
    submenu.querySelector('[data-mobile-back]')?.addEventListener('click', () => parent.focus());
  });

  const search = document.querySelector('.search-bar');
  const searchOpener = document.querySelector('[data-search-open]');
  if (search && searchOpener) {
    search.id = 'storefront-search';
    searchOpener.setAttribute('aria-controls', search.id);
    let wasOpen = false;
    const sync = () => {
      const open = search.classList.contains('is-active');
      search.inert = !open;
      searchOpener.setAttribute('aria-expanded', String(open));
      if (open && !wasOpen) search.querySelector('input')?.focus();
      if (!open && wasOpen) searchOpener.focus();
      wasOpen = open;
    };
    sync();
    new MutationObserver(sync).observe(search, { attributes: true, attributeFilter: ['class'] });
  }

  document.querySelectorAll('.header__nav-item').forEach(item => {
    item.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        item.classList.add('is-dismissed');
        item.querySelector('.header__nav-link').focus();
      }
    });
    item.addEventListener('mouseenter', () => item.classList.remove('is-dismissed'));
    item.addEventListener('focusout', event => {
      if (!item.contains(event.relatedTarget)) item.classList.remove('is-dismissed');
    });
  });

  // Keep a calculator estimate with its quote request, without storing contact data.
  const calculator = document.getElementById('railing-calculator');
  if (calculator) {
    calculator.querySelectorAll('.calc-toggle-group').forEach(group => {
      group.setAttribute('role', 'group');
      const label = group.parentElement.querySelector('.form-label');
      if (label) group.setAttribute('aria-label', label.textContent.trim());
      const sync = () => group.querySelectorAll('.calc-toggle').forEach(button => button.setAttribute('aria-pressed', String(button.classList.contains('is-active'))));
      sync();
      group.addEventListener('click', () => queueMicrotask(sync));
    });
    calculator.addEventListener('click', event => {
      const link = event.target.closest('a[href^="/pages/quote"]');
      if (!link) return;
      const length = Number(calculator.querySelector('#calc-length')?.value);
      if (!Number.isFinite(length) || length <= 0) return;
      const url = new URL(link.href);
      url.searchParams.set('length', length);
      const height = calculator.querySelector('#height-group .is-active')?.dataset.height;
      const finish = calculator.querySelector('#finish-group .is-active')?.dataset.finish;
      if (height) url.searchParams.set('height', height);
      if (finish) url.searchParams.set('finish', finish);
      link.href = url.pathname + url.search;
    });
  }
  const quoteLength = document.getElementById('qf-length');
  if (quoteLength) {
    const params = new URLSearchParams(window.location.search);
    const length = Number(params.get('length'));
    if (!quoteLength.value && Number.isFinite(length) && length > 0 && length <= 100000) quoteLength.value = length;
    const height = document.getElementById('qf-height');
    if (height && !height.value && ['36', '42', '48'].includes(params.get('height'))) {
      const option = [...height.options].find(item => item.value.startsWith(params.get('height') + '"'));
      if (option) height.value = option.value;
    }
    const finish = document.getElementById('qf-finish');
    if (finish && !finish.value) {
      const value = { stainless: 'Brushed Stainless', black: 'Matte Black' }[params.get('finish')];
      if (value) finish.value = value;
    }
  }
})();
